import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowRight,
  BarChart3,
  BellRing,
  CalendarCheck,
  CheckCircle2,
  ClipboardList,
  CreditCard,
  DollarSign,
  TrendingUp,
  WalletCards,
  XCircle,
  type LucideIcon
} from "lucide-react";
import type { DashboardSummary } from "@booking-seconds/types";
import { formatCurrency } from "@booking-seconds/utils";
import { apiFetch } from "../../lib/api";

type Tone = "emerald" | "amber" | "rose" | "blue" | "violet" | "slate";

const toneClasses: Record<Tone, { icon: string; bar: string; text: string; surface: string }> = {
  amber: {
    icon: "tw:bg-amber-50 tw:text-amber-700 tw:ring-amber-100",
    bar: "tw:bg-amber-500",
    text: "tw:text-amber-700",
    surface: "tw:bg-amber-50 tw:border-amber-100"
  },
  blue: {
    icon: "tw:bg-blue-50 tw:text-blue-700 tw:ring-blue-100",
    bar: "tw:bg-blue-500",
    text: "tw:text-blue-700",
    surface: "tw:bg-blue-50 tw:border-blue-100"
  },
  emerald: {
    icon: "tw:bg-emerald-50 tw:text-emerald-700 tw:ring-emerald-100",
    bar: "tw:bg-emerald-500",
    text: "tw:text-emerald-700",
    surface: "tw:bg-emerald-50 tw:border-emerald-100"
  },
  rose: {
    icon: "tw:bg-rose-50 tw:text-rose-700 tw:ring-rose-100",
    bar: "tw:bg-rose-500",
    text: "tw:text-rose-700",
    surface: "tw:bg-rose-50 tw:border-rose-100"
  },
  slate: {
    icon: "tw:bg-slate-100 tw:text-slate-700 tw:ring-slate-200",
    bar: "tw:bg-slate-500",
    text: "tw:text-slate-700",
    surface: "tw:bg-slate-50 tw:border-slate-200"
  },
  violet: {
    icon: "tw:bg-violet-50 tw:text-violet-700 tw:ring-violet-100",
    bar: "tw:bg-violet-500",
    text: "tw:text-violet-700",
    surface: "tw:bg-violet-50 tw:border-violet-100"
  }
};

function DashboardCard({
  title,
  eyebrow,
  actions,
  children,
  className = ""
}: {
  title: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`tw:rounded-lg tw:border tw:border-slate-200/80 tw:bg-white/85 tw:p-4 tw:shadow-[0_12px_30px_rgba(15,23,42,0.08)] ${className}`}>
      <div className="tw:mb-4 tw:flex tw:items-start tw:justify-between tw:gap-3">
        <div className="tw:min-w-0">
          {eyebrow ? <p className="tw:m-0 tw:text-[0.68rem] tw:font-bold tw:uppercase tw:tracking-[0.16em] tw:text-slate-500">{eyebrow}</p> : null}
          <h2 className="tw:m-0 tw:mt-1 tw:text-lg tw:font-bold tw:leading-tight tw:text-slate-950">{title}</h2>
        </div>
        {actions ? <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-2">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

function IconMetricCard({
  label,
  value,
  detail,
  icon: Icon,
  tone
}: {
  label: string;
  value: ReactNode;
  detail: string;
  icon: LucideIcon;
  tone: Tone;
}) {
  return (
    <div className="tw:rounded-lg tw:border tw:border-slate-200/80 tw:bg-white/90 tw:p-4 tw:shadow-[0_10px_24px_rgba(15,23,42,0.06)]">
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-3">
        <div className="tw:min-w-0">
          <p className="tw:m-0 tw:text-sm tw:font-semibold tw:text-slate-500">{label}</p>
          <p className="tw:m-0 tw:mt-2 tw:text-2xl tw:font-bold tw:leading-tight tw:text-slate-950">{value}</p>
        </div>
        <span className={`tw:grid tw:size-10 tw:shrink-0 tw:place-items-center tw:rounded-lg tw:ring-1 ${toneClasses[tone].icon}`}>
          <Icon className="tw:size-5" aria-hidden="true" />
        </span>
      </div>
      <div className={`tw:mt-4 tw:h-1 tw:w-12 tw:rounded-full ${toneClasses[tone].bar}`} />
      <p className="tw:m-0 tw:mt-3 tw:text-sm tw:text-slate-500">{detail}</p>
    </div>
  );
}

function QuickLink({ to, icon: Icon, label }: { to: string; icon: LucideIcon; label: string }) {
  return (
    <Link
      to={to}
      className="tw:inline-flex tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-slate-200 tw:bg-white tw:px-3 tw:py-2 tw:text-sm tw:font-bold tw:text-slate-800 tw:shadow-sm tw:transition tw:hover:border-slate-300 tw:hover:bg-slate-50"
    >
      <Icon className="tw:size-4" aria-hidden="true" />
      <span>{label}</span>
    </Link>
  );
}

function ProgressRow({
  label,
  value,
  detail,
  percent,
  tone = "slate"
}: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  percent: number;
  tone?: Tone;
}) {
  const clampedPercent = Math.max(3, Math.min(100, Number.isFinite(percent) ? percent : 0));

  return (
    <div>
      <div className="tw:flex tw:items-baseline tw:justify-between tw:gap-3">
        <div className="tw:min-w-0">
          <p className="tw:m-0 tw:text-sm tw:font-bold tw:text-slate-900">{label}</p>
          {detail ? <p className="tw:m-0 tw:mt-0.5 tw:text-xs tw:text-slate-500">{detail}</p> : null}
        </div>
        <strong className="tw:shrink-0 tw:text-sm tw:text-slate-950">{value}</strong>
      </div>
      <div className="tw:mt-2 tw:h-2 tw:overflow-hidden tw:rounded-full tw:bg-slate-100">
        <div className={`tw:h-full tw:rounded-full ${toneClasses[tone].bar}`} style={{ width: `${clampedPercent}%` }} />
      </div>
    </div>
  );
}

function AttentionRow({
  label,
  value,
  detail,
  icon: Icon,
  tone
}: {
  label: string;
  value: ReactNode;
  detail: string;
  icon: LucideIcon;
  tone: Tone;
}) {
  return (
    <div className={`tw:flex tw:items-center tw:gap-3 tw:rounded-lg tw:border tw:p-3 ${toneClasses[tone].surface}`}>
      <span className={`tw:grid tw:size-9 tw:shrink-0 tw:place-items-center tw:rounded-lg tw:bg-white/80 ${toneClasses[tone].text}`}>
        <Icon className="tw:size-4" aria-hidden="true" />
      </span>
      <div className="tw:min-w-0 tw:flex-1">
        <div className="tw:flex tw:items-baseline tw:justify-between tw:gap-3">
          <p className="tw:m-0 tw:text-sm tw:font-bold tw:text-slate-950">{label}</p>
          <strong className="tw:shrink-0 tw:text-sm tw:text-slate-950">{value}</strong>
        </div>
        <p className="tw:m-0 tw:mt-0.5 tw:text-xs tw:text-slate-600">{detail}</p>
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <div className="tw:grid tw:min-h-36 tw:place-items-center tw:rounded-lg tw:border tw:border-dashed tw:border-slate-200 tw:bg-slate-50/70 tw:p-5 tw:text-center">
      <div>
        <span className="tw:mx-auto tw:grid tw:size-10 tw:place-items-center tw:rounded-lg tw:bg-white tw:text-slate-500 tw:ring-1 tw:ring-slate-200">
          <Icon className="tw:size-5" aria-hidden="true" />
        </span>
        <p className="tw:m-0 tw:mt-3 tw:text-sm tw:font-semibold tw:text-slate-600">{children}</p>
      </div>
    </div>
  );
}

function formatInteger(value: number) {
  return new Intl.NumberFormat("en-US").format(value);
}

export function DashboardPage() {
  const summary = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => apiFetch<DashboardSummary>("/api/dashboard/summary")
  });

  if (summary.isLoading) {
    return (
      <div className="tw:rounded-lg tw:border tw:border-slate-200 tw:bg-white/80 tw:p-5 tw:text-sm tw:font-semibold tw:text-slate-600">
        Loading dashboard...
      </div>
    );
  }

  if (summary.error || !summary.data) {
    return (
      <div className="tw:rounded-lg tw:border tw:border-rose-200 tw:bg-rose-50 tw:p-5 tw:text-sm tw:font-semibold tw:text-rose-700">
        Dashboard summary could not be loaded.
      </div>
    );
  }

  const data = summary.data;
  const totalStatusBookings = Math.max(data.pendingBookings + data.confirmedBookings + data.cancelledBookings, 1);
  const maxRevenue = Math.max(data.revenueToday, data.revenueWeek, data.revenueMonth, 1);
  const maxServiceRevenue = Math.max(...data.topServices.map((service) => service.totalRevenue), 1);
  const notificationsCount = data.notifications.length;
  const hasAttention = data.pendingBookings > 0 || data.pendingPayments > 0 || data.cancelledBookings > 0 || notificationsCount > 0;
  const todayLabel = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric"
  }).format(new Date());

  return (
    <div className="tw:space-y-4">
      <section className="tw:rounded-lg tw:border tw:border-slate-200/80 tw:bg-white/90 tw:p-4 tw:shadow-[0_12px_30px_rgba(15,23,42,0.08)]">
        <div className="tw:flex tw:flex-col tw:gap-4 tw:xl:flex-row tw:xl:items-center tw:xl:justify-between">
          <div className="tw:min-w-0">
            <p className="tw:m-0 tw:text-[0.68rem] tw:font-bold tw:uppercase tw:tracking-[0.18em] tw:text-emerald-700">Operations dashboard</p>
            <h1 className="tw:m-0 tw:mt-1 tw:text-3xl tw:font-bold tw:leading-tight tw:text-slate-950">BookingSeconds</h1>
            <p className="tw:m-0 tw:mt-2 tw:max-w-3xl tw:text-sm tw:text-slate-600">
              {todayLabel} snapshot for booking volume, revenue, payment follow-up, and dispatch attention.
            </p>
          </div>
          <div className="tw:flex tw:flex-wrap tw:gap-2">
            <QuickLink to="/admin/bookings" icon={ClipboardList} label="Bookings" />
            <QuickLink to="/admin/payments" icon={CreditCard} label="Payments" />
            <QuickLink to="/admin/customers" icon={CalendarCheck} label="Customers" />
            <QuickLink to="/admin/reports" icon={BarChart3} label="Reports" />
          </div>
        </div>
      </section>

      <div className="tw:grid tw:gap-3 tw:md:grid-cols-2 tw:xl:grid-cols-4">
        <IconMetricCard label="Bookings today" value={formatInteger(data.bookingsToday)} detail="Scheduled service volume" icon={CalendarCheck} tone="blue" />
        <IconMetricCard label="Pending" value={formatInteger(data.pendingBookings)} detail="Waiting for confirmation" icon={AlertCircle} tone="amber" />
        <IconMetricCard label="Confirmed" value={formatInteger(data.confirmedBookings)} detail="Active confirmed bookings" icon={CheckCircle2} tone="emerald" />
        <IconMetricCard label="Pending payments" value={formatCurrency(data.pendingPayments)} detail="Open customer balances" icon={WalletCards} tone="violet" />
      </div>

      <div className="tw:grid tw:gap-4 tw:xl:grid-cols-[1.15fr_0.85fr]">
        <DashboardCard title="Revenue snapshot" eyebrow="Current totals" actions={<TrendingUp className="tw:size-5 tw:text-emerald-700" aria-hidden="true" />}>
          <div className="tw:space-y-4">
            <ProgressRow label="Today" value={formatCurrency(data.revenueToday)} detail="Bookings dated today" percent={(data.revenueToday / maxRevenue) * 100} tone="emerald" />
            <ProgressRow label="Last 7 days" value={formatCurrency(data.revenueWeek)} detail="Rolling service revenue" percent={(data.revenueWeek / maxRevenue) * 100} tone="blue" />
            <ProgressRow label="Last 30 days" value={formatCurrency(data.revenueMonth)} detail="Monthly operating view" percent={(data.revenueMonth / maxRevenue) * 100} tone="violet" />
          </div>
        </DashboardCard>

        <DashboardCard title="Operations health" eyebrow="Needs attention" actions={<BellRing className="tw:size-5 tw:text-amber-700" aria-hidden="true" />}>
          {hasAttention ? (
            <div className="tw:space-y-3">
              <AttentionRow label="Pending bookings" value={formatInteger(data.pendingBookings)} detail="Confirm or follow up before dispatch." icon={AlertCircle} tone="amber" />
              <AttentionRow label="Pending payments" value={formatCurrency(data.pendingPayments)} detail="Review balances and payment queue." icon={DollarSign} tone="violet" />
              <AttentionRow label="Cancelled bookings" value={formatInteger(data.cancelledBookings)} detail="Watch for revenue leakage and rebooking chances." icon={XCircle} tone="rose" />
              <AttentionRow label="Notification failures" value={formatInteger(notificationsCount)} detail="Retries and customer communication follow-up." icon={BellRing} tone="blue" />
            </div>
          ) : (
            <EmptyState icon={CheckCircle2}>No operational follow-up is waiting right now.</EmptyState>
          )}
        </DashboardCard>
      </div>

      <div className="tw:grid tw:gap-4 tw:xl:grid-cols-[1fr_1fr]">
        <DashboardCard title="Booking status mix" eyebrow="Pipeline">
          <div className="tw:grid tw:gap-3 tw:md:grid-cols-3">
            <AttentionRow
              label="Pending"
              value={formatInteger(data.pendingBookings)}
              detail={`${Math.round((data.pendingBookings / totalStatusBookings) * 100)}% of tracked bookings`}
              icon={AlertCircle}
              tone="amber"
            />
            <AttentionRow
              label="Confirmed"
              value={formatInteger(data.confirmedBookings)}
              detail={`${Math.round((data.confirmedBookings / totalStatusBookings) * 100)}% of tracked bookings`}
              icon={CheckCircle2}
              tone="emerald"
            />
            <AttentionRow
              label="Cancelled"
              value={formatInteger(data.cancelledBookings)}
              detail={`${Math.round((data.cancelledBookings / totalStatusBookings) * 100)}% of tracked bookings`}
              icon={XCircle}
              tone="rose"
            />
          </div>
        </DashboardCard>

        <DashboardCard title="Top services" eyebrow="Revenue leaders">
          {data.topServices.length ? (
            <div className="tw:space-y-4">
              {data.topServices.map((service, index) => (
                <ProgressRow
                  key={`${service.name}-${index}`}
                  label={service.name}
                  value={formatCurrency(service.totalRevenue)}
                  detail={`${formatInteger(service.totalBookings)} booking${service.totalBookings === 1 ? "" : "s"}`}
                  percent={(service.totalRevenue / maxServiceRevenue) * 100}
                  tone={index === 0 ? "emerald" : index === 1 ? "blue" : "slate"}
                />
              ))}
            </div>
          ) : (
            <EmptyState icon={BarChart3}>Top services will appear after bookings have line-item revenue.</EmptyState>
          )}
        </DashboardCard>
      </div>

      <DashboardCard
        title="Notification follow-up"
        eyebrow="Failures and retries"
        actions={
          <Link to="/admin/communications" className="tw:inline-flex tw:items-center tw:gap-1 tw:text-sm tw:font-bold tw:text-slate-700 tw:hover:text-slate-950">
            Communications <ArrowRight className="tw:size-4" aria-hidden="true" />
          </Link>
        }
      >
        {data.notifications.length ? (
          <div className="tw:grid tw:gap-2">
            {data.notifications.map((item, index) => (
              <div key={`${item.type}-${index}`} className="tw:flex tw:items-start tw:gap-3 tw:rounded-lg tw:border tw:border-rose-100 tw:bg-rose-50 tw:p-3">
                <span className="tw:grid tw:size-8 tw:shrink-0 tw:place-items-center tw:rounded-lg tw:bg-white tw:text-rose-700">
                  <BellRing className="tw:size-4" aria-hidden="true" />
                </span>
                <div className="tw:min-w-0">
                  <p className="tw:m-0 tw:text-sm tw:font-bold tw:text-slate-950">{item.type.replace(/_/g, " ")}</p>
                  <p className="tw:m-0 tw:mt-0.5 tw:text-sm tw:text-slate-600">{item.message}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState icon={CheckCircle2}>No failed notifications are waiting for attention.</EmptyState>
        )}
      </DashboardCard>
    </div>
  );
}
